import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './TeachersModule.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 12;

const TEACHER_SUBJECTS = [
  'Arabic', 'Art', 'Biology', 'Chemistry', 'Computer Science', 'English',
  'French', 'Geography', 'History', 'Mathematics', 'Music', 'Physical Education',
  'Physics', 'Science', 'Other'
].sort();

const TeachersModule = ({ school }) => {
  const { t } = useTranslation('landing');
  const tm = (key) => t(`business.schoolDashboard.teachersModule.${key}`);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [employmentFilter, setEmploymentFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [profileTab, setProfileTab] = useState('basic');
  const [profileAttendance, setProfileAttendance] = useState(null);
  const [profileSalary, setProfileSalary] = useState(null);
  const [profileLogs, setProfileLogs] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showAssignClassModal, setShowAssignClassModal] = useState(null);
  const [schoolClasses, setSchoolClasses] = useState([]);
  const [assignClassId, setAssignClassId] = useState('');
  const [newClassName, setNewClassName] = useState('');
  const [addForm, setAddForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    subject: '',
    employmentType: 'full_time',
    hireDate: '',
    baseSalary: '',
    employeeId: '',
    password: '',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  const getToken = () => sessionStorage.getItem('token') || localStorage.getItem('token');

  const fetchStaff = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ role: 'teacher' });
    if (statusFilter) params.set('status', statusFilter);
    if (employmentFilter) params.set('employment_type', employmentFilter);
    if (subjectFilter.trim()) params.set('subject', subjectFilter.trim());
    if (search.trim()) params.set('search', search.trim());
    if (sortBy) params.set('sort', sortBy === 'attendance' ? 'attendance' : sortBy === 'salary' ? 'salary_status' : 'name');
    fetch(`${API_BASE_URL}/api/schools/staff?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.staff) setStaff(data.staff);
        else setError(data.message || 'Failed to load teachers');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [statusFilter, employmentFilter, subjectFilter, search, sortBy]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const openProfile = (s) => {
    const token = getToken();
    if (!token) return;
    setProfileTab('basic');
    setProfileAttendance(null);
    setProfileSalary(null);
    setProfileLogs(null);
    fetch(`${API_BASE_URL}/api/schools/staff/${s.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setSelectedStaff(data.staff);
      })
      .catch(() => setError('Failed to load profile'));
  };

  const fetchProfileExtra = (staffId, tab) => {
    const token = getToken();
    if (!token || !staffId) return;
    if (tab === 'attendance') {
      fetch(`${API_BASE_URL}/api/schools/staff/${staffId}/attendance`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => d.success && setProfileAttendance(d));
    } else if (tab === 'salary') {
      fetch(`${API_BASE_URL}/api/schools/staff/${staffId}/salary`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => d.success && setProfileSalary(d));
    } else if (tab === 'logs') {
      fetch(`${API_BASE_URL}/api/schools/staff/${staffId}/logs`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => d.success && setProfileLogs(d));
    }
  };

  const openAssignClass = (s) => {
    setShowAssignClassModal(s);
    setAssignClassId('');
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE_URL}/api/schools/classes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => d.success && setSchoolClasses(d.classes || []));
  };

  const submitAssignClass = () => {
    if (!showAssignClassModal || !assignClassId) return;
    const token = getToken();
    if (!token) return;
    setSaving(true);
    fetch(`${API_BASE_URL}/api/schools/staff/${showAssignClassModal.id}/classes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ classId: parseInt(assignClassId, 10) })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setShowAssignClassModal(null);
          fetchStaff();
          if (selectedStaff && selectedStaff.id === showAssignClassModal.id) openProfile(showAssignClassModal);
        } else setError(d.message || 'Failed to assign class');
      })
      .finally(() => setSaving(false));
  };

  const createClassAndAssign = () => {
    if (!newClassName.trim() || !showAssignClassModal) return;
    const token = getToken();
    if (!token) return;
    setSaving(true);
    fetch(`${API_BASE_URL}/api/schools/classes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: newClassName.trim() })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.classId) {
          return fetch(`${API_BASE_URL}/api/schools/staff/${showAssignClassModal.id}/classes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ classId: d.classId })
          }).then((r2) => r2.json());
        }
        throw new Error(d.message || 'Failed to create class');
      })
      .then((d2) => {
        if (d2.success) {
          setShowAssignClassModal(null);
          setNewClassName('');
          fetchStaff();
        } else setError(d2.message || 'Failed to assign class');
      })
      .catch((err) => setError(err.message))
      .finally(() => setSaving(false));
  };

  const openEdit = (s) => {
    setEditingStaff(s);
    setShowAddModal(true);
    setAddForm({
      email: s.email || '',
      firstName: s.firstName || '',
      lastName: s.lastName || '',
      phone: s.phone || '',
      subject: s.subject || '',
      employmentType: s.employmentType || 'full_time',
      hireDate: s.hireDate ? (typeof s.hireDate === 'string' ? s.hireDate.slice(0, 10) : s.hireDate) : '',
      baseSalary: s.baseSalary != null ? String(s.baseSalary) : '',
      employeeId: s.employeeId || '',
      password: '',
      status: s.status || 'active'
    });
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    setSaving(true);
    if (editingStaff) {
      fetch(`${API_BASE_URL}/api/schools/staff/${editingStaff.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subject: addForm.subject,
          employmentType: addForm.employmentType,
          hireDate: addForm.hireDate || null,
          baseSalary: addForm.baseSalary ? parseFloat(addForm.baseSalary) : null,
          employeeId: addForm.employeeId,
          status: addForm.status
        })
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setShowAddModal(false);
            setEditingStaff(null);
            setAddForm({ email: '', firstName: '', lastName: '', phone: '', subject: '', employmentType: 'full_time', hireDate: '', baseSalary: '', employeeId: '', password: '' });
            fetchStaff();
            if (selectedStaff && selectedStaff.id === editingStaff.id) openProfile({ id: editingStaff.id });
          } else setError(data.message || 'Failed to update');
        })
        .catch((err) => setError(err.message))
        .finally(() => setSaving(false));
      return;
    }
    fetch(`${API_BASE_URL}/api/schools/staff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...addForm, staffRole: 'teacher' })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setShowAddModal(false);
          setAddForm({ email: '', firstName: '', lastName: '', phone: '', subject: '', employmentType: 'full_time', hireDate: '', baseSalary: '', employeeId: '', password: '' });
          fetchStaff();
        } else setError(data.message || 'Failed to add teacher');
      })
      .catch((err) => setError(err.message))
      .finally(() => setSaving(false));
  };

  const removeTeacher = (id) => {
    if (!window.confirm('Remove this teacher from the school?')) return;
    const token = getToken();
    fetch(`${API_BASE_URL}/api/schools/staff/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSelectedStaff(null);
          fetchStaff();
        } else setError(data.message || 'Failed to remove');
      });
  };

  return (
    <div className="teachers-module">
      <div className="teachers-module-toolbar">
        <div className="teachers-module-search">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder={tm('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="teachers-module-filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{tm('allStatuses')}</option>
            <option value="active">{tm('active')}</option>
            <option value="on_leave">{tm('onLeave')}</option>
            <option value="inactive">{tm('inactive')}</option>
          </select>
          <select value={employmentFilter} onChange={(e) => setEmploymentFilter(e.target.value)}>
            <option value="">{tm('allTypes')}</option>
            <option value="full_time">{tm('fullTime')}</option>
            <option value="part_time">{tm('partTime')}</option>
          </select>
        </div>
        <select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
          <option value="">{tm('allSubjects')}</option>
          {[...new Set(staff.map((s) => s.subject).filter(Boolean))].sort().map((sub) => (
            <option key={sub} value={sub}>{sub}</option>
          ))}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="name">{tm('sortName')}</option>
          <option value="attendance">{tm('sortAttendance')}</option>
          <option value="salary">{tm('sortSalary')}</option>
        </select>
        <button type="button" className="teachers-module-add-btn" onClick={() => { setEditingStaff(null); setShowAddModal(true); setAddForm({ email: '', firstName: '', lastName: '', phone: '', subject: '', employmentType: 'full_time', hireDate: '', baseSalary: '', employeeId: '', password: '', status: 'active' }); }}>
          <span className="material-icons">person_add</span> {tm('addTeacher')}
        </button>
      </div>

      {error && (
        <div className="teachers-module-error">
          <span className="material-icons">error_outline</span> {error}
        </div>
      )}

      {loading ? (
        <div className="teachers-module-loading">
          <span className="material-icons spin">refresh</span>
          <p>{tm('loading')}</p>
        </div>
      ) : (
        <>
          <div className="teachers-module-content">
            {staff.length === 0 ? (
              <p className="teachers-module-empty">{tm('noTeachersYet')}</p>
            ) : (
              <div className="teachers-module-cards">
                {staff.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((s) => (
                <div key={s.id} className="teachers-module-card">
                  <div className="teachers-module-card-avatar">
                    <span className="material-icons">person</span>
                  </div>
                  <h3 className="teachers-module-card-name">{s.fullName}</h3>
                  <p className="teachers-module-card-subject">{s.subject || '—'}</p>
                  <p className="teachers-module-card-meta">
                    {s.employmentType === 'full_time' ? tm('fullTime') : tm('partTime')} ·{' '}
                    <span className={`teachers-module-status teachers-module-status-${s.status}`}>{s.status === 'on_leave' ? tm('onLeave') : s.status === 'inactive' ? tm('inactive') : tm('active')}</span>
                  </p>
                  {s.assignedClasses && s.assignedClasses.length > 0 && (
                    <div className="teachers-module-card-badges">
                      {s.assignedClasses.map((c) => (
                        <span key={c.id} className="teachers-module-badge">{c.name}</span>
                      ))}
                    </div>
                  )}
                  <div className="teachers-module-card-stats">
                    {s.attendanceRate30 != null && <span title="Attendance (30d)"><span className="material-icons">event_available</span> {s.attendanceRate30}%</span>}
                    {s.lastSalaryStatus && <span className={`teachers-module-salary-status teachers-module-salary-${s.lastSalaryStatus}`} title="Salary">{s.lastSalaryStatus}</span>}
                  </div>
                  <div className="teachers-module-card-actions">
                    <button type="button" onClick={() => openProfile(s)} title={tm('view')}><span className="material-icons">visibility</span> {tm('view')}</button>
                    <button type="button" onClick={() => openEdit(s)} title={tm('edit')}><span className="material-icons">edit</span> {tm('edit')}</button>
                    <button type="button" onClick={() => openAssignClass(s)} title={tm('assign')}><span className="material-icons">class</span> {tm('assign')}</button>
                    <button type="button" onClick={() => { openProfile(s); setProfileTab('performance'); }} title={tm('performance')}><span className="material-icons">trending_up</span> {tm('performance')}</button>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
          {staff.length > PAGE_SIZE && (
            <div className="teachers-module-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{tm('previous')}</button>
              <span>{tm('page')} {page} of {Math.ceil(staff.length / PAGE_SIZE)}</span>
              <button type="button" disabled={page >= Math.ceil(staff.length / PAGE_SIZE)} onClick={() => setPage((p) => p + 1)}>{tm('next')}</button>
            </div>
          )}
        </>
      )}

      {showAddModal && (
        <div className="teachers-module-modal-overlay" onClick={() => { setShowAddModal(false); setEditingStaff(null); }}>
          <div className="teachers-module-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingStaff ? tm('editTeacherTitle') : tm('addTeacherTitle')}</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="teachers-module-form-row">
                <label>{tm('firstName')} *</label>
                <input
                  required
                  value={addForm.firstName}
                  onChange={(e) => setAddForm((p) => ({ ...p, firstName: e.target.value }))}
                />
              </div>
              <div className="teachers-module-form-row">
                <label>{tm('lastName')} *</label>
                <input
                  required
                  value={addForm.lastName}
                  onChange={(e) => setAddForm((p) => ({ ...p, lastName: e.target.value }))}
                />
              </div>
              {!editingStaff && (
                <div className="teachers-module-form-row">
                  <label>{tm('email')} *</label>
                  <input
                    type="email"
                    required
                    value={addForm.email}
                    onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
              )}
              <div className="teachers-module-form-row">
                <label>{tm('phone')}</label>
                <input
                  value={addForm.phone}
                  onChange={(e) => setAddForm((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className="teachers-module-form-row">
                <label>{tm('subject')}</label>
                <select
                  value={addForm.subject}
                  onChange={(e) => setAddForm((p) => ({ ...p, subject: e.target.value }))}
                >
                  <option value="">{tm('selectSubject')}</option>
                  {[...new Set([...TEACHER_SUBJECTS, addForm.subject].filter(Boolean))].sort().map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
              <div className="teachers-module-form-row">
                <label>{tm('employmentType')}</label>
                <select
                  value={addForm.employmentType}
                  onChange={(e) => setAddForm((p) => ({ ...p, employmentType: e.target.value }))}
                >
                  <option value="full_time">{tm('fullTime')}</option>
                  <option value="part_time">{tm('partTime')}</option>
                </select>
              </div>
              <div className="teachers-module-form-row">
                <label>{tm('hireDate')}</label>
                <input
                  type="date"
                  value={addForm.hireDate}
                  onChange={(e) => setAddForm((p) => ({ ...p, hireDate: e.target.value }))}
                />
              </div>
              <div className="teachers-module-form-row">
                <label>{tm('baseSalary')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={addForm.baseSalary}
                  onChange={(e) => setAddForm((p) => ({ ...p, baseSalary: e.target.value }))}
                />
              </div>
              <div className="teachers-module-form-row">
                <label>{tm('employeeId')}</label>
                <input
                  value={addForm.employeeId}
                  onChange={(e) => setAddForm((p) => ({ ...p, employeeId: e.target.value }))}
                />
              </div>
              {editingStaff && (
                <div className="teachers-module-form-row">
                  <label>{tm('status')}</label>
                  <select value={addForm.status} onChange={(e) => setAddForm((p) => ({ ...p, status: e.target.value }))}>
                    <option value="active">{tm('active')}</option>
                    <option value="on_leave">{tm('onLeave')}</option>
                    <option value="inactive">{tm('inactive')}</option>
                  </select>
                </div>
              )}
              {!editingStaff && (
                <div className="teachers-module-form-row">
                  <label>{tm('password')} (optional)</label>
                  <input
                    type="password"
                    value={addForm.password}
                    onChange={(e) => setAddForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder="Min 8 chars for new users"
                  />
                </div>
              )}
              <div className="teachers-module-modal-actions">
                <button type="button" onClick={() => { setShowAddModal(false); setEditingStaff(null); }}>{tm('cancel')}</button>
                <button type="submit" disabled={saving}>{saving ? '…' : editingStaff ? tm('save') : tm('addTeacher')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedStaff && (
        <div className="teachers-module-modal-overlay" onClick={() => setSelectedStaff(null)}>
          <div className="teachers-module-modal teachers-module-profile-modal teachers-module-profile-wide" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedStaff.fullName}</h3>
            <div className="teachers-module-profile-tabs">
              {['basic', 'attendance', 'salary', 'performance', 'logs'].map((tab) => (
                <button key={tab} type="button" className={profileTab === tab ? 'active' : ''} onClick={() => { setProfileTab(tab); if (['attendance', 'salary', 'logs'].includes(tab)) fetchProfileExtra(selectedStaff.id, tab); }}>{tm(tab === 'performance' ? 'performance' : tab)}</button>
              ))}
            </div>
            {profileTab === 'basic' && (
              <div className="teachers-module-profile-section">
                <p><strong>Subject:</strong> {selectedStaff.subject || '—'}</p>
                <p><strong>Employment:</strong> {selectedStaff.employmentType === 'full_time' ? 'Full-time' : 'Part-time'}</p>
                <p><strong>Status:</strong> <span className={`teachers-module-status teachers-module-status-${selectedStaff.status}`}>{selectedStaff.status}</span></p>
                <p><strong>Email:</strong> {selectedStaff.email}</p>
                <p><strong>Phone:</strong> {selectedStaff.phone || '—'}</p>
                {selectedStaff.attendanceRate30 != null && <p><strong>Attendance (30d):</strong> {selectedStaff.attendanceRate30}%</p>}
                {selectedStaff.lastSalary && (
                  <p><strong>Last salary:</strong> <span className={`teachers-module-salary-status teachers-module-salary-${selectedStaff.lastSalary.payment_status}`}>{selectedStaff.lastSalary.payment_status}</span> {selectedStaff.lastSalary.final_salary != null && `— ${selectedStaff.lastSalary.final_salary}`}</p>
                )}
                <p><strong>Assigned classes:</strong> {selectedStaff.assignedClasses?.length ? selectedStaff.assignedClasses.map((c) => c.name).join(', ') : 'None'}</p>
              </div>
            )}
            {profileTab === 'attendance' && (
              <div className="teachers-module-profile-section">
                {!profileAttendance && <p>Loading attendance…</p>}
                {profileAttendance && (
                  <>
                    <p><strong>Year:</strong> {profileAttendance.year}</p>
                    {profileAttendance.byMonth && profileAttendance.byMonth.length > 0 ? (
                      <table className="teachers-module-table">
                        <thead><tr><th>Month</th><th>Present</th><th>Late</th><th>Absent</th><th>Rate %</th></tr></thead>
                        <tbody>
                          {profileAttendance.byMonth.map((m) => (
                            <tr key={m.month}><td>{m.month}</td><td>{m.present}</td><td>{m.late}</td><td>{m.absent}</td><td>{m.rate ?? '—'}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    ) : <p>No attendance records for this year.</p>}
                  </>
                )}
              </div>
            )}
            {profileTab === 'salary' && (
              <div className="teachers-module-profile-section">
                {!profileSalary && <p>Loading salary history…</p>}
                {profileSalary && profileSalary.salaryHistory && (
                  profileSalary.salaryHistory.length > 0 ? (
                    <table className="teachers-module-table">
                      <thead><tr><th>Period</th><th>Base</th><th>Bonus</th><th>Deduction</th><th>Final</th><th>Status</th><th>Payment date</th></tr></thead>
                      <tbody>
                        {profileSalary.salaryHistory.map((row) => (
                          <tr key={row.id}><td>{row.period}</td><td>{row.baseSalary}</td><td>{row.bonus}</td><td>{row.deduction}</td><td>{row.finalSalary}</td><td><span className={`teachers-module-salary-status teachers-module-salary-${row.paymentStatus}`}>{row.paymentStatus}</span></td><td>{row.paymentDate || '—'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p>No salary history.</p>
                )}
              </div>
            )}
            {profileTab === 'performance' && (
              <div className="teachers-module-profile-section">
                <p>Performance metrics (ranking, student scores, supervisor evaluation) will appear here once configured.</p>
              </div>
            )}
            {profileTab === 'logs' && (
              <div className="teachers-module-profile-section">
                {!profileLogs && <p>Loading activity log…</p>}
                {profileLogs && profileLogs.logs && (
                  profileLogs.logs.length > 0 ? (
                    <ul className="teachers-module-logs">
                      {profileLogs.logs.map((log) => (
                        <li key={log.id}><strong>{log.actionType}</strong> — {log.description} <em>{log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}</em></li>
                      ))}
                    </ul>
                  ) : <p>No activity log.</p>
                )}
              </div>
            )}
            <div className="teachers-module-modal-actions">
              <button type="button" onClick={() => removeTeacher(selectedStaff.id)} className="teachers-module-btn-danger">Remove from school</button>
              <button type="button" onClick={() => setSelectedStaff(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showAssignClassModal && (
        <div className="teachers-module-modal-overlay" onClick={() => setShowAssignClassModal(null)}>
          <div className="teachers-module-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Assign Class — {showAssignClassModal.fullName}</h3>
            <div className="teachers-module-form-row">
              <label>Class</label>
              <select value={assignClassId} onChange={(e) => setAssignClassId(e.target.value)}>
                <option value="">Select a class</option>
                {schoolClasses.filter((c) => !showAssignClassModal.assignedClasses?.some((a) => a.id === c.id)).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.level ? ` (${c.level})` : ''}</option>
                ))}
              </select>
            </div>
            {schoolClasses.length === 0 && (
              <div className="teachers-module-form-row">
                <label>Or create a new class</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={newClassName} onChange={(e) => setNewClassName(e.target.value)} placeholder="e.g. 1AP" style={{ flex: 1 }} />
                  <button type="button" disabled={!newClassName.trim() || saving} onClick={createClassAndAssign}>{saving ? '…' : 'Create & assign'}</button>
                </div>
              </div>
            )}
            <div className="teachers-module-modal-actions">
              <button type="button" onClick={() => setShowAssignClassModal(null)}>Cancel</button>
              <button type="button" disabled={!assignClassId || saving} onClick={submitAssignClass}>{saving ? 'Assigning…' : 'Assign'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeachersModule;
